

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Typography,
} from "@mui/material";
import { Users, CheckCircle, XCircle, Edit, Eye, Trash, Search } from "lucide-react";
import "./UserManagement.css"; // rename to OrganizationManagement.css if you prefer
import { useOrganizations } from "../../hooks/org/useOrganizations";
import { useAddOrganization } from "../../hooks/org/useAddOrganization";
import { useDeleteOrganization } from "../../hooks/org/useDeleteOrganization";
import { updateOrganizationApi } from "../../apis/organizationApi";
import FormDialog from "../../assets/FormDialog";

// Helper to get initials
const getInitials = (name) => {
  if (!name || typeof name !== "string" || name.trim() === "") return "??";
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "??";
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Format organization data
const formatOrganization = (org, currentUser) => {
  if (!org) return null;

  const isClientAdmin = currentUser?.role === "client_admin";
  const myOrgId = currentUser?.organization_id || currentUser?.organization;

  if (isClientAdmin && org.id !== myOrgId) return null;

  return {
    id: org.id,
    name: org.name || "",
    code: org.code || "",
    contact_email: org.contact_email || "",
    contact_phone: org.contact_phone || "",
    is_active: org.is_active ?? true,
    address: org.address || "",
    city: org.city || "",
    state: org.state || "",
    country: org.country || "",
    postal_code: org.postal_code || "",
    created_at: org.created_at || "",
    last_updated: org.updated_at
      ? new Date(org.updated_at).toLocaleString("en-IN", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—",
    logo: org.logo || null,
  };
};

const OrganizationManagement = ({ user }) => {
  const [filters, setFilters] = useState({ status: "" });
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const { organizations: rawOrganizations, loading, error, refetch } =
    useOrganizations(filters, currentPage, entriesPerPage);

  const [organizations, setOrganizations] = useState([]);

  const { addOrganization } = useAddOrganization();
  const { deleteOrganization } = useDeleteOrganization();

  const [isAddOrgOpen, setIsAddOrgOpen] = useState(false);
  const [isEditOrgOpen, setIsEditOrgOpen] = useState(false);
  const [isViewOrgOpen, setIsViewOrgOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);

  useEffect(() => {
    if (rawOrganizations) {
      const formatted = rawOrganizations
        .map((org) => formatOrganization(org, user))
        .filter(Boolean);
      setOrganizations(formatted);
    }
  }, [rawOrganizations, user]);

  const filteredOrganizations = organizations.filter((org) => {
    const search = activeSearch.toLowerCase().trim();
    if (!search) return true;
    return (
      org.name?.toLowerCase().includes(search) ||
      org.contact_email?.toLowerCase().includes(search) ||
      org.code?.toLowerCase().includes(search)
    );
  });

  const paginatedOrganizations = filteredOrganizations.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const handleSearchCommit = () => {
    setActiveSearch(query.trim());
    setCurrentPage(1);
  };

  const handleClear = () => {
    setQuery("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  // ── Form Fields ──
  const orgFields = [
    {
      name: "name",
      label: "Organization Name",
      type: "text",
      required: true,
      gridWidth: 12,
      group: "Basic Info",
    },
    {
      name: "code",
      label: "Organization Code",
      type: "text",
      gridWidth: 12,
      group: "Basic Info",
    },
    // {
    //   name: "status",
    //   label: "Status",
    //   type: "select",
    //   required: true,
    //   gridWidth: 6,
    //   group: "Basic Info",
    //   options: [
    //     { value: "active", label: "Active" },
    //     { value: "inactive", label: "Inactive" },
    //   ],
    //   defaultValue: "active",
    // },
    {
      name: "logo",
      label: "Logo",
      type: "file",
      gridWidth: 6,
      group: "Basic Info",
      inputProps: { accept: "image/jpeg,image/jpg,image/png" },
    },
    {
      name: "contact_email",
      label: "Contact Email",
      type: "email",
      required: true,
      gridWidth: 6,
      group: "Contact Details",
    },
    {
      name: "contact_phone",
      label: "Contact Phone",
      type: "text",
      required: true,
      gridWidth: 6,
      group: "Contact Details",
      validate: (v) => {
        if (!v) return "Required";
        if (/\s/.test(v)) return "Spaces not allowed";
        if (!/^[6-9]\d{9}$/.test(v)) return "Please enter a valid 10-digit Indian mobile number";
        return "";
      },
      inputProps: { maxLength: 10, autoComplete: "off" },
    },
    {
      name: "address",
      label: "Address",
      type: "multiline",
      rows: 3,
      required: true,
      gridWidth: 12,
      group: "Address Information",
    },
    {
      name: "city",
      label: "City",
      type: "text",
      required: true,
      gridWidth: 6,
      group: "Address Information",
    },
    {
      name: "state",
      label: "State",
      type: "text",
      required: true,
      gridWidth: 6,
      group: "Address Information",
    },
    {
      name: "country",
      label: "Country",
      type: "text",
      required: true,
      gridWidth: 6,
      group: "Address Information",
    },
    {
      name: "postal_code",
      label: "Postal Code (Pincode)",
      type: "text",
      required: true,
      gridWidth: 6,
      group: "Address Information",
      validate: (v) => (!/^\d{6}$/.test(v || "") ? "Pincode must be exactly 6 digits" : ""),
      inputProps: { maxLength: 6 },
    },
  ];

  // Fields allowed to edit via PATCH /organizations/{id}/
  const editableInEditMode = [
    "name",
    "address",
    "city",
    "state",
    "country",
    "postal_code",
    "contact_email",
    "contact_phone",
    "logo",
  ];

  const viewOrgFields = [
    { name: "name", label: "Organization Name", type: "text", gridWidth: 12, group: "Basic Info" },
    { name: "code", label: "Organization Code", type: "text", gridWidth: 12, group: "Basic Info" },
    // {
    //   name: "is_active",
    //   label: "Status",
    //   type: "text",
    //   gridWidth: 6,
    //   group: "Basic Info",
    //   render: (val) => (val ? "Active" : "Inactive"),
    // },
    {
      name: "logo",
      label: "Logo",
      type: "image",
      gridWidth: 6,
      group: "Basic Info",
      render: (value) =>
        value ? (
          <img
            src={value}
            alt="Organization Logo"
            style={{
              maxWidth: "160px",
              maxHeight: "160px",
              objectFit: "contain",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          />
        ) : (
          <Typography color="text.secondary">No logo uploaded</Typography>
        ),
    },
    { name: "contact_email", label: "Contact Email", type: "text", gridWidth: 6, group: "Contact Details" },
    { name: "contact_phone", label: "Contact Phone", type: "text", gridWidth: 6, group: "Contact Details" },
    { name: "address", label: "Address", type: "multiline", rows: 3, gridWidth: 12, group: "Address Information" },
    { name: "city", label: "City", type: "text", gridWidth: 6, group: "Address Information" },
    { name: "state", label: "State", type: "text", gridWidth: 6, group: "Address Information" },
    { name: "country", label: "Country", type: "text", gridWidth: 6, group: "Address Information" },
    { name: "postal_code", label: "Postal Code", type: "text", gridWidth: 6, group: "Address Information" },
  ];

      // ── Inside OrganizationManagement component ──

    // Add / Create
    const handleAddOrgSubmit = async (data) => {
      try {
        const payload = {
          ...data,
          is_active: data.status === "active",
          logo: data.logo instanceof File ? data.logo : undefined, // backend may accept File or ignore
        };
        delete payload.status; // remove frontend-only field

        await addOrganization(payload);           // ← assume this sends JSON
        refetch();
        setIsAddOrgOpen(false);
      } catch (err) {
        const errorMsg =
          err?.response?.data?.detail ||
          err?.response?.data?.non_field_errors?.[0] ||
          Object.values(err?.response?.data || {})[0]?.[0] ||
          err?.message ||
          "Unknown error";
        // alert(`Failed to add organization: ${errorMsg}`);
      }
    };

    // Edit / Update
    const handleEditOrgSubmit = async (data) => {
      try {
        const payload = {
          ...data,
          is_active: data.status === "active",
          logo: data.logo instanceof File ? data.logo : undefined,
        };
        delete payload.status;

        // Only send fields actually allowed by PATCH
        const allowedPayload = {};
        editableInEditMode.forEach((key) => {
          if (payload.hasOwnProperty(key)) {
            allowedPayload[key] = payload[key];
          }
        });
        if (payload.is_active !== undefined) {
          allowedPayload.is_active = payload.is_active;
        }

        await updateOrganizationApi(selectedOrgId, allowedPayload);
        refetch();
        setIsEditOrgOpen(false);
      } catch (err) {
        const errorMsg =
          err?.response?.data?.detail ||
          err?.response?.data?.non_field_errors?.[0] ||
          Object.values(err?.response?.data || {})[0]?.[0] ||
          err?.message ||
          "Unknown error";
        // alert(`Failed to update organization: ${errorMsg}`);
      }
    };
  const selectedOrg = organizations.find((o) => o.id === selectedOrgId) || {};

  const editInitialValues = {
    ...selectedOrg,
    status: selectedOrg.is_active ? "active" : "inactive",
  };

  return (
    <form autoComplete="off">
      <div className="user-management">
        {/* Summary Cards */}
        <div className="summary-section">
          <div className="summary-card summary-card--blue">
            <div className="summary-card__icon"><Users size={24} color="white" /></div>
            <div className="summary-card__content">
              <div className="summary-card__title">Total Organizations</div>
              <div className="summary-card__value">{filteredOrganizations.length}</div>
            </div>
          </div>
          <div className="summary-card summary-card--green">
            <div className="summary-card__icon"><CheckCircle size={24} color="white" /></div>
            <div className="summary-card__content">
              <div className="summary-card__title">Active Organizations</div>
              <div className="summary-card__value">{filteredOrganizations.filter(o => o.is_active).length}</div>
            </div>
          </div>
          <div className="summary-card summary-card--teal">
            <div className="summary-card__icon"><XCircle size={24} color="white" /></div>
            <div className="summary-card__content">
              <div className="summary-card__title">Inactive Organizations</div>
              <div className="summary-card__value">{filteredOrganizations.filter(o => !o.is_active).length}</div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="controls-section">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Name, email, code..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearchCommit())}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small" onClick={handleSearchCommit} edge="start">
                        <Search size={16} color="#94a3b8" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment: query && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleClear} edge="end">
                        <XCircle size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                name="status"
                value={filters.status}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, status: e.target.value }));
                  setCurrentPage(1);
                }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={() => setIsAddOrgOpen(true)}
                sx={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #9DD368 0%, #5A9130 100%)",
                }}
              >
                Add Organization
              </Button>
            </Grid>
          </Grid>
        </div>

        {/* Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-state">Loading organizations...</div>
          ) : error ? (
            <div className="error-state">Failed to load organizations. Please try again.</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Code</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrganizations.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <div className="user-info">
                        <div
                          className="user-avatar"
                          style={{
                            backgroundColor: org.logo ? "transparent" : "#3b82f6",
                          }}
                        >
                          {org.logo ? (
                            <img
                              src={org.logo}
                              alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
                              onError={(e) => { e.target.src = ''; }}
                            />
                          ) : (
                            getInitials(org.name)
                          )}
                        </div>
                        <div className="user-details">
                          <div className="user-name">{org.name || "Unnamed"}</div>
                          <div className="user-email">{org.contact_email || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${org.is_active ? "status-active" : "status-inactive"}`}>
                        {org.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>{org.code || "—"}</td>
                    <td>{org.last_updated}</td>
                    <td>
                      <IconButton size="small" onClick={() => { setSelectedOrgId(org.id); setIsEditOrgOpen(true); }}>
                        <Edit size={20} color="#10b981" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setSelectedOrgId(org.id); setIsViewOrgOpen(true); }}>
                        <Eye size={20} color="#06b6d4" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setOrgToDelete(org.id); setIsDeleteDialogOpen(true); }}>
                        <Trash size={20} color="#ef4444" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {paginatedOrganizations.length === 0 && !loading && (
            <div className="empty-state">
              <svg viewBox="0 0 24 24" className="empty-icon" fill="#6b7280">
                <path d="M12 12c2.21 0 4-1.79 4-4S14.21 4 12 4 8 5.79 8 8s1.79 4 4 4zm0 2c-3.33 0-10 1.67-10 5v3h20v-3c0-3.33-6.67-5-10-5z" />
              </svg>
              <h3>No organizations found</h3>
              <p>Try adjusting your search or filters.</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredOrganizations.length > 0 && (
          <div className="pagination-section">
            <div className="pagination-info">
              Showing {(currentPage - 1) * entriesPerPage + 1} –{" "}
              {Math.min(currentPage * entriesPerPage, filteredOrganizations.length)} of{" "}
              {filteredOrganizations.length} organizations
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage * entriesPerPage >= filteredOrganizations.length}
              >
                Next
              </button>

              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="page-size-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <FormDialog
          open={isAddOrgOpen}
          onClose={() => setIsAddOrgOpen(false)}
          title="Add New Organization"
          fields={orgFields}
          initialValues={{ status: "active" }}
          onSubmit={handleAddOrgSubmit}
          submitLabel="Create Organization"
        />

        <FormDialog
          open={isEditOrgOpen}
          onClose={() => setIsEditOrgOpen(false)}
          title="Edit Organization"
          fields={orgFields.map(field => ({
            ...field,
            // Disable fields not allowed in PATCH
            disabled: !editableInEditMode.includes(field.name) && field.name !== "logo",
          }))}
          initialValues={editInitialValues}
          onSubmit={handleEditOrgSubmit}
          profileImage={selectedOrg.logo}
          profileName={selectedOrg.name}
          showProfileEdit={false}
          editMode={true}           // ← helps FormDialog know it's edit (if you use that prop)
        />

        <FormDialog
          open={isViewOrgOpen}
          onClose={() => setIsViewOrgOpen(false)}
          title="Organization Details"
          fields={viewOrgFields}
          initialValues={selectedOrg}
          viewMode={true}
          submitLabel="Close"
          cancelLabel="Close"
          profileImage={selectedOrg.logo}
          profileName={selectedOrg.name}
          showProfileEdit={true}
          onProfileEdit={() => {
            setIsViewOrgOpen(false);
            setIsEditOrgOpen(true);
          }}
        />

        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this organization? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={async () => {
                try {
                  await deleteOrganization(orgToDelete);
                  refetch();
                  setIsDeleteDialogOpen(false);
                } catch (err) {
                  // alert("Failed to delete organization");
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </form>
  );
};

export default OrganizationManagement;